import React from "react";
import { Clock, Pencil, Tag } from "lucide-react";

/**
 * Bande visuelle représentant une plage horaire réservée dans une colonne
 * jour de l'Agenda départements. Pleine largeur, fond translucide (~22%),
 * bordure gauche colorée pour identification rapide.
 *
 * Props :
 *   - slot       : objet plage (title, color, start_time, end_time, description, category_id)
 *   - categories : liste des shift_categories (pour afficher le libellé de la catégorie)
 *   - canEdit    : bool, active le hover cursor + clic → onEdit
 *   - onEdit     : callback (slot) => void
 */
export default function AgendaSlotBand({ slot, categories, canEdit, onEdit }) {
  const color = slot.color || "#7C3AED";
  const cat = (categories || []).find((c) => c.id === slot.category_id);
  return (
    <div
      className={`rounded-sm border-l-[3px] px-3 py-2 ${canEdit ? "cursor-pointer hover:brightness-95" : ""}`}
      style={{
        backgroundColor: `${color}22`, // ≈13% alpha en hex
        borderLeftColor: color,
      }}
      onClick={() => canEdit && onEdit && onEdit(slot)}
      role={canEdit ? "button" : undefined}
      tabIndex={canEdit ? 0 : undefined}
      onKeyDown={(e) => {
        if (canEdit && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onEdit?.(slot);
        }
      }}
      data-testid={`agenda-slot-band-${slot.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-zinc-800 truncate">
            {slot.title}
          </p>
          <div className="mt-0.5 flex items-center gap-2 flex-wrap text-[11px] font-mono text-zinc-600">
            <span className="inline-flex items-center gap-1">
              <Clock size={10} className="text-zinc-400" />
              {slot.start_time}–{slot.end_time}
            </span>
            {cat && (
              <span className="inline-flex items-center gap-1 text-zinc-500">
                <Tag size={10} className="text-zinc-400" />
                {cat.name}
              </span>
            )}
          </div>
          {slot.description ? (
            <p className="mt-1 text-[11px] text-zinc-500 line-clamp-2">
              {slot.description}
            </p>
          ) : null}
        </div>
        {canEdit && (
          <Pencil
            size={12}
            className="text-zinc-400 shrink-0 mt-0.5"
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}
